import { z } from "zod";
import type { Component, Site } from "@/wab/shared/model/classes";
import { publishingPrintContextSchema, resolvePublishingPrintContext } from "./print-context";
import { getBookLeaves, readBook } from "./book";
import {
  derivePageSides,
  matchSpreadAnchor,
  startSideToPhysical,
} from "./binding";
import { resolvePageBoxes } from "./boxes";
import { guideExtent, resolvePageLayout } from "./guides";
import { DIGITAL_PRESENTATIONS } from "./digital-presentation";
import { resolveMasterAssignment } from "./masters";
import { printMarksSchema, validatePrintMarksGeometry } from "./print-marks";

export const DOCUMENT_KEY = "airtisan.publishing.v2";
export const PUBLISHING_VIEW_KEY = "airtisan.publishing-view.v1";
const id = z.string().min(1).max(500);
export const publishingViewSchema = z
  .object({
    id,
    frames: z.record(id),
    masterFrames: z.record(z.record(id)).default({}),
  })
  .strict()
  .superRefine((view, ctx) => {
    const frames = [
      ...Object.values(view.frames),
      ...Object.values(view.masterFrames).flatMap(Object.values),
    ];
    if (new Set(frames).size !== frames.length) {
      ctx.addIssue({
        code: "custom",
        message: "Each page content or master occurrence needs its own frame",
      });
    }
  });
export type PublishingView = z.infer<typeof publishingViewSchema>;
export const PUBLISHING_MASTER_VIEW_KEY = "airtisan.publishing-master-view.v1";
export const publishingMasterViewSchema = z.object({
  id,
  selectedMasterId: id.optional(),
  frames: z.record(z.record(z.object({ left: id.optional(), right: id.optional() }).strict())),
}).strict().superRefine((view, ctx) => {
  const frames = Object.values(view.frames).flatMap((items) => Object.values(items).flatMap((sides) => Object.values(sides)));
  if (new Set(frames).size !== frames.length) ctx.addIssue({ code: "custom", message: "Each master sheet item needs its own frame" });
});
export type PublishingMasterView = z.infer<typeof publishingMasterViewSchema>;

const mm = z.number().finite().min(0).max(1000);
const trimSchema = z.object({ width: mm.min(40), height: mm.min(40) }).strict();
const edgesSchema = z
  .object({ top: mm, right: mm, bottom: mm, left: mm })
  .strict();
const marginsSchema = z
  .object({ top: mm, outer: mm, bottom: mm, inner: mm })
  .strict();
const geometrySchema = z
  .object({
    trim: trimSchema,
    bleed: edgesSchema,
    slug: edgesSchema,
    margins: marginsSchema,
  })
  .strict();
const printStateSchema = z
  .object({
    variantIds: z.array(id).max(200),
    globalVariantIds: z.array(id).max(200),
    printContext: publishingPrintContextSchema.optional(),
  })
  .strict();
const positionSchema = z
  .object({
    x: z.number().finite().min(-1000).max(2000),
    y: z.number().finite().min(-1000).max(2000),
  })
  .strict();
const guideBaseSchema = z.object({
  id,
  /** x runs horizontally across the trim, y vertically down it. */
  axis: z.enum(["x", "y"]),
  /** Raw millimetres from the trim's top-left corner (or from the declared
   * inner/outer edge, inward-positive). Negative values reach into bleed/slug;
   * the allowed range is checked against the resolved page boxes. */
  position: z.number().finite().min(-1000).max(2000),
  /** Optional declared binding-relative intent, coherent only on the x axis. */
  origin: z.enum(["inner", "outer"]).optional(),
});
const guideOriginRefinement = (
  guide: { axis: "x" | "y"; origin?: "inner" | "outer" },
  ctx: z.RefinementCtx,
) => {
  if (guide.origin !== undefined && guide.axis !== "x")
    ctx.addIssue({
      code: "custom",
      message: "Inner/outer guide origin applies only to the x axis",
    });
};
const guideSchema = guideBaseSchema
  .strict()
  .superRefine(guideOriginRefinement);
const gridSchema = z
  .object({
    id,
    columns: z
      .object({
        count: z.number().int().min(1).max(48),
        gutter: mm,
        width: mm.optional(),
      })
      .strict()
      .optional(),
    baseline: z
      .object({ step: mm.min(0.5), offset: mm.optional() })
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((grid, ctx) => {
    if (grid.columns === undefined && grid.baseline === undefined)
      ctx.addIssue({
        code: "custom",
        message: "Grid must define columns or a baseline",
      });
  });
const layoutAnnotationsSchema = z
  .object({
    guides: z.array(guideSchema).max(500).optional(),
    grids: z.array(gridSchema).max(500).optional(),
  })
  .strict();
const guideOverrideSchema = z
  .object({
    guideId: id,
    hidden: z.boolean().optional(),
    position: z.number().finite().min(-1000).max(2000).optional(),
  })
  .strict()
  .superRefine((override, ctx) => {
    if (override.hidden === undefined && override.position === undefined)
      ctx.addIssue({
        code: "custom",
        message: "Guide override must hide or move its target",
      });
  });
const spreadGuideSchema = z
  .object({
    id,
    axis: z.enum(["x", "y"]),
    /** Millimetres from the spread's visual-left trim edge (x) or trim top (y). */
    position: z.number().finite().min(-1000).max(4000),
    /** Occurrence ids only; the derived spread id is never persisted. */
    anchor: z
      .object({ leftPageId: id.optional(), rightPageId: id.optional() })
      .strict(),
  })
  .strict();
const masterItemSchema = z
  .object({
    id,
    componentId: id,
    side: z.enum(["both", "left", "right"]),
    position: positionSchema,
    layer: z.enum(["below", "above"]),
    printState: printStateSchema,
  })
  .strict();
const masterSchema = layoutAnnotationsSchema
  .extend({
    id,
    title: id,
    basedOn: id.optional(),
    geometry: geometrySchema.partial(),
    items: z.array(masterItemSchema).max(200),
  })
  .strict();
const masterOverrideSchema = z
  .object({
    itemId: id,
    hidden: z.boolean().optional(),
    componentId: id.optional(),
    position: positionSchema.optional(),
    printState: printStateSchema.optional(),
  })
  .strict()
  .superRefine((override, ctx) => {
    if (
      override.hidden === undefined &&
      override.componentId === undefined &&
      override.position === undefined &&
      override.printState === undefined
    )
      ctx.addIssue({
        code: "custom",
        message: "Master override must specify a change",
      });
    if (override.componentId !== undefined && override.printState === undefined)
      ctx.addIssue({
        code: "custom",
        message: "Replacement master content needs explicit print state",
      });
  });
const sectionSchema = z
  .object({
    id,
    title: id.optional(),
    unitId: id.optional(),
    /** Occurrence where this section begins; never a physical index. */
    startPageId: id,
    /** Persisted constraint, checked against the derived side on every write. */
    startSide: z.enum(["recto", "verso"]).optional(),
    numbering: z
      .object({
        style: z.enum(["decimal", "none"]),
        /** Explicit first label number, or continue the running count. */
        start: z.union([z.number().int().min(1).max(1_000_000), z.literal("continue")]),
      })
      .strict(),
  })
  .strict();
const pageSchema = layoutAnnotationsSchema
  .extend({
    id,
    componentId: id.nullable(),
    unitId: id.optional(),
    lessonId: id.optional(),
    masterId: id.optional(),
    masterOverrides: z.array(masterOverrideSchema).max(1000).optional(),
    guideOverrides: z.array(guideOverrideSchema).max(500).optional(),
    geometry: geometrySchema.partial(),
    printState: printStateSchema,
  })
  .strict();
export const publishingDocumentSchema = z
  .object({
    version: z.union([z.literal(2), z.literal(3)]),
    title: id,
    defaults: geometrySchema,
    binding: z
      .object({
        direction: z.enum(["ltr", "rtl"]),
        firstSide: z.enum(["left", "right"]),
        facing: z.boolean(),
        startNumber: z.number().int().min(1),
      })
      .strict(),
    units: z
      .array(
        z
          .object({
            id,
            title: id,
            lessons: z.array(z.object({ id, title: id }).strict()).max(200),
          })
          .strict(),
      )
      .max(200),
    pages: z.array(pageSchema).max(2000),
    masters: z.array(masterSchema).max(200).optional(),
    guides: z.array(guideSchema).max(500).optional(),
    grids: z.array(gridSchema).max(500).optional(),
    /** Guides pinned to page occurrences of one derived spread. Revalidated
     * whenever binding or page order changes, so a split anchor fails closed. */
    spreadGuides: z.array(spreadGuideSchema).max(500).optional(),
    sections: z.array(sectionSchema).max(200).optional(),
    printMarks: printMarksSchema.optional(),
    /** Optional reader policy. Absent means responsive and is never written implicitly. */
    digital: z
      .object({ presentation: z.enum(DIGITAL_PRESENTATIONS) })
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((doc, ctx) => {
    const contexts = [
      ...doc.pages.map(page => page.printState.printContext),
      ...(doc.masters ?? []).flatMap(master => master.items.map(item => item.printState.printContext)),
      ...doc.pages.flatMap(page => (page.masterOverrides ?? []).map(item => item.printState?.printContext)),
    ];
    if (doc.version === 2 && contexts.some(context => context !== undefined))
      ctx.addIssue({ code: "custom", message: "Explicit print context requires publishing document version 3" });
    for (const page of doc.pages) {
      if (page.componentId === null && page.printState.printContext !== undefined)
        ctx.addIssue({ code: "custom", message: "Blank page cannot carry a root print context" });
    }
    const unique = (values: string[], kind: string) => {
      if (new Set(values).size !== values.length) {
        ctx.addIssue({ code: "custom", message: `${kind} IDs must be unique` });
      }
    };
    unique(
      doc.pages.map((p) => p.id),
      "Page occurrence",
    );
    unique(
      doc.units.map((u) => u.id),
      "Unit",
    );
    unique(
      doc.units.flatMap((u) => u.lessons.map((l) => l.id)),
      "Lesson",
    );
    const annotationIds = (entries?: { id: string }[]) =>
      (entries ?? []).map((entry) => entry.id);
    unique(annotationIds(doc.guides), "Guide");
    unique(annotationIds(doc.grids), "Grid");
    unique(annotationIds(doc.spreadGuides), "Spread guide");
    unique(annotationIds(doc.sections), "Section");
    const pageIds = new Set(doc.pages.map((p) => p.id));
    for (const guide of doc.spreadGuides ?? []) {
      const match = matchSpreadAnchor(doc, guide.anchor);
      if ("error" in match) {
        ctx.addIssue({
          code: "custom",
          message: `Spread guide ${guide.id} ${match.error}`,
        });
        continue;
      }
      try {
        const geometryOf = (pageId?: string) => {
          const page = pageId
            ? doc.pages.find((p) => p.id === pageId)
            : undefined;
          return page ? resolveMasterAssignment(doc, page).geometry : undefined;
        };
        const left = geometryOf(match.spread.leftPageId);
        const right = geometryOf(match.spread.rightPageId);
        const present = [left, right].filter(
          (g): g is PublishingGeometry => !!g,
        );
        // x runs from the visual-left trim edge across the present pages
        // (zero gutter); y must fit every present page.
        const { min, max } =
          guide.axis === "x"
            ? {
                min: guideExtent(present[0], "x").min,
                max:
                  (right ? (left?.trim.width ?? 0) : 0) +
                  guideExtent(present[present.length - 1], "x").max,
              }
            : {
                min: Math.max(...present.map((g) => guideExtent(g, "y").min)),
                max: Math.min(...present.map((g) => guideExtent(g, "y").max)),
              };
        if (guide.position < min || guide.position > max)
          ctx.addIssue({
            code: "custom",
            message: `Spread guide ${guide.id} lies outside the spread media (${min}..${max} mm)`,
          });
      } catch (error) {
        ctx.addIssue({ code: "custom", message: (error as Error).message });
      }
    }
    unique(
      (doc.sections ?? []).map((s) => s.startPageId),
      "Section start",
    );
    const pageSides = derivePageSides(doc);
    for (const section of doc.sections ?? []) {
      const index = doc.pages.findIndex((p) => p.id === section.startPageId);
      if (index < 0) {
        ctx.addIssue({
          code: "custom",
          message: `Section ${section.id} starts at a missing occurrence`,
        });
        continue;
      }
      if (section.unitId !== undefined && !doc.units.some((u) => u.id === section.unitId))
        ctx.addIssue({
          code: "custom",
          message: `Section ${section.id} links a missing unit`,
        });
      if (section.startSide !== undefined) {
        if (!doc.binding.facing)
          ctx.addIssue({
            code: "custom",
            message: `Section ${section.id} startSide requires facing pages`,
          });
        else if (
          pageSides[index] !==
          startSideToPhysical(section.startSide, doc.binding.direction)
        )
          ctx.addIssue({
            code: "custom",
            message: `Section ${section.id} must start on a ${section.startSide} page but its start occurrence derives to the ${pageSides[index]} side`,
          });
      }
    }
    const checkGeometry = (g: PublishingGeometry) => {
      if (
        g.margins.inner + g.margins.outer >= g.trim.width ||
        g.margins.top + g.margins.bottom >= g.trim.height
      ) {
        ctx.addIssue({
          code: "custom",
          message: "Margins leave no content area",
        });
      }
    };
    checkGeometry(doc.defaults);
    const checkLayout = (
      owner: string,
      layout: ReturnType<typeof resolvePageLayout>,
      geometry: PublishingGeometry,
      side: "left" | "right",
    ) => {
      for (const guide of layout.guides) {
        const extent = guideExtent(geometry, guide.axis);
        if (guide.positionMm < extent.min || guide.positionMm > extent.max)
          ctx.addIssue({
            code: "custom",
            message: `Guide ${guide.id} on ${owner} lies outside the page media (${extent.min}..${extent.max} mm)`,
          });
      }
      // Grids are defined over the content box: trim minus the side-resolved
      // inner/outer and top/bottom margins. Bounds follow the resolved box,
      // never a hardcoded trim or margin value.
      const content = resolvePageBoxes(geometry, side).content;
      for (const grid of layout.grids) {
        if (
          grid.columns &&
          ((grid.columns.count - 1) * grid.columns.gutter >= content.width ||
            (grid.columns.width !== undefined &&
              grid.columns.count * grid.columns.width +
                (grid.columns.count - 1) * grid.columns.gutter >
                content.width + 1e-6))
        )
          ctx.addIssue({
            code: "custom",
            message: `Grid ${grid.id} on ${owner} has columns wider than the content box`,
          });
        if (
          grid.baseline &&
          (grid.baseline.step > content.height ||
            (grid.baseline.offset ?? 0) > content.height)
        )
          ctx.addIssue({
            code: "custom",
            message: `Grid ${grid.id} on ${owner} has a baseline outside the content box`,
          });
      }
    };
    unique(
      (doc.masters ?? []).map((m) => m.id),
      "Master",
    );
    for (const master of doc.masters ?? []) {
      unique(
        master.items.map((i) => i.id),
        "Master item",
      );
      unique(annotationIds(master.guides), "Master guide");
      unique(annotationIds(master.grids), "Master grid");
      for (const item of master.items) {
        unique(item.printState.variantIds, "Master print variant");
        unique(item.printState.globalVariantIds, "Master global print variant");
      }
      // Validate even currently unassigned chains, rather than deferring a
      // dangling/cyclic definition until the user assigns it to a page.
      try {
        const probe = {
          id: "validation",
          componentId: null,
          geometry: {},
          printState: { variantIds: [], globalVariantIds: [] },
          masterId: master.id,
        };
        const geometry = resolveMasterAssignment(doc, probe).geometry;
        checkGeometry(geometry);
        for (const side of ["left", "right"] as const)
          checkLayout(
            `master ${master.id}`,
            resolvePageLayout(doc, probe, side, geometry),
            geometry,
            side,
          );
      } catch (error) {
        ctx.addIssue({ code: "custom", message: (error as Error).message });
      }
    }
    for (const [pageIndex, page] of doc.pages.entries()) {
      unique(
        (page.masterOverrides ?? []).map((o) => o.itemId),
        "Master override",
      );
      unique(annotationIds(page.guides), "Page guide");
      unique(annotationIds(page.grids), "Page grid");
      for (const override of page.masterOverrides ?? []) {
        if (override.printState) {
          unique(override.printState.variantIds, "Override print variant");
          unique(
            override.printState.globalVariantIds,
            "Override global print variant",
          );
        }
      }
      try {
        const geometry = resolveMasterAssignment(doc, page).geometry;
        checkGeometry(geometry);
        validatePrintMarksGeometry(geometry, doc.printMarks);
        unique(
          (page.guideOverrides ?? []).map((o) => o.guideId),
          "Guide override",
        );
        checkLayout(
          `page ${page.id}`,
          resolvePageLayout(doc, page, pageSides[pageIndex], geometry),
          geometry,
          pageSides[pageIndex],
        );
      } catch (error) {
        ctx.addIssue({ code: "custom", message: (error as Error).message });
      }
      if (page.unitId !== undefined || page.lessonId !== undefined) {
        if (
          !doc.units
            .find((u) => u.id === page.unitId)
            ?.lessons.some((l) => l.id === page.lessonId)
        ) {
          ctx.addIssue({
            code: "custom",
            message: `Page ${page.id} has invalid unit/lesson membership`,
          });
        }
      }
      unique(page.printState.variantIds, "Print variant");
      unique(page.printState.globalVariantIds, "Print global variant");
      if (
        page.componentId === null &&
        (page.printState.variantIds.length ||
          page.printState.globalVariantIds.length)
      ) {
        ctx.addIssue({
          code: "custom",
          message: "Blank pages cannot select content variants",
        });
      }
    }
  });
export type PublishingGeometry = z.infer<typeof geometrySchema>;
export type PublishingGuide = z.infer<typeof guideSchema>;
export type PublishingGrid = z.infer<typeof gridSchema>;
export type PublishingSpreadGuide = z.infer<typeof spreadGuideSchema>;
export type PublishingSection = z.infer<typeof sectionSchema>;
export type PublishingDocument = z.infer<typeof publishingDocumentSchema>;
export type PublishingPage = PublishingDocument["pages"][number];
export type PublishingSite = {
  flags: { [key: string]: string | boolean | number | null | undefined };
  components: {
    uuid: string;
    name: string;
    metadata: { [key: string]: string };
    variants?: { uuid: string }[];
    variantGroups?: { multi?: boolean; variants: { uuid: string }[] }[];
  }[];
  globalVariantGroups?: {
    multi?: boolean;
    type?: string;
    variants: { uuid: string }[];
  }[];
};

/** Read only: migration is never a side effect of reading an ordinary project. */
export function readPublishingDocument(
  site: Pick<PublishingSite, "flags">,
): PublishingDocument | undefined {
  const raw = site.flags[DOCUMENT_KEY];
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw !== "string") throw new Error("Invalid publishing document");
  return publishingDocumentSchema.parse(JSON.parse(raw));
}

/** Native editors must preserve the meaning of retained print selections.
 * The caller supplies global choices whose source it will actually change.
 * Reading legacy/canonical metadata is pure; no book is initialized or rewritten. */
export function getPublishingGlobalVariantEditConflicts(
  site: PublishingSite,
  changedVariantIds: Iterable<string>,
): string[] {
  const changed = new Set(changedVariantIds);
  if (!changed.size) {
    return [];
  }
  const document = migrateLegacyBook(site);
  if (!document) {
    return [];
  }
  const selections = [
    ...document.pages.map((page) => page.printState),
    ...(document.masters ?? []).flatMap((master) =>
      master.items.map((item) => item.printState),
    ),
    ...document.pages.flatMap((page) =>
      (page.masterOverrides ?? []).flatMap((override) =>
        override.printState ? [override.printState] : [],
      ),
    ),
  ];
  return [
    ...new Set(
      selections
        .flatMap((state) => state.globalVariantIds)
        .filter((variantId) => changed.has(variantId)),
    ),
  ];
}

/** Preflight native removal against a projected source inventory. No metadata
 * or native objects are changed; a group must pass before its first mutation.
 */
export function assertPublishingReferencesSurviveRemoval(
  site: PublishingSite,
  removal: { componentIds?: string[]; variantIds?: string[]; paramIds?: string[] },
) {
  // A new scalar-param preflight must not broaden legacy component edits.
  // Only explicit v3 metadata can retain these new references.
  const paramsOnly = removal.paramIds !== undefined && removal.componentIds === undefined && removal.variantIds === undefined;
  if (paramsOnly) {
    const raw = site.flags[DOCUMENT_KEY];
    if (typeof raw !== "string") return;
    let version: unknown;
    try { version = JSON.parse(raw)?.version; } catch { return; }
    if (version !== 3) return;
  }
  const doc = migrateLegacyBook(site);
  if (!doc) return;
  const removedParams = new Set(removal.paramIds ?? []);
  if (removedParams.size) {
    const states = [
      ...doc.pages.map(page => page.printState),
      ...(doc.masters ?? []).flatMap(master => master.items.map(item => item.printState)),
      ...doc.pages.flatMap(page => (page.masterOverrides ?? []).flatMap(item => item.printState ? [item.printState] : [])),
    ];
    if (states.some(state => state.printContext?.props.some(prop => removedParams.has(prop.paramId))))
      throw new Error("Cannot delete a native parameter retained by a publishing print context. Remove or replace its explicit assignment first.");
  }
  if (paramsOnly) return;
  const components = new Set(removal.componentIds ?? []);
  const variants = new Set(removal.variantIds ?? []);
  const filterVariants = <V extends { uuid: string }>(values: V[]) =>
    values.filter((value) => !variants.has(value.uuid));
  try {
    validatePublishingReferences(
      {
        ...site,
        components: site.components
          .filter((c) => !components.has(c.uuid))
          .map((c) => ({
            ...c,
            variants: filterVariants(c.variants ?? []),
            variantGroups: c.variantGroups?.map((group) => ({
              ...group,
              variants: filterVariants(group.variants),
            })),
          })),
        globalVariantGroups: site.globalVariantGroups?.map((group) => ({
          ...group,
          variants: filterVariants(group.variants),
        })),
      },
      doc,
    );
  } catch (error) {
    throw new Error(
      `Cannot delete content with publishing references: ${error instanceof Error ? error.message : "invalid reference"}. Remove or replace its publishing assignment first.`,
    );
  }
}

export function validatePublishingReferences(
  site: PublishingSite,
  doc: PublishingDocument,
) {
  const globals = new Set(
    site.globalVariantGroups?.flatMap((g) => g.variants.map((v) => v.uuid)) ??
      [],
  );
  const validateChoices = (
    groups: { multi?: boolean; type?: string; variants: { uuid: string }[] }[],
    selected: string[],
    pageId: string,
  ) => {
    const active = new Set(selected);
    for (const group of groups) {
      // Responsive breakpoint activation can overlap. It is not a named
      // single-choice prop and must not be normalized into one.
      if (group.multi || group.type === "global-screen") continue;
      if (group.variants.filter((v) => active.has(v.uuid)).length > 1) {
        throw new Error(
          `Page ${pageId} selects conflicting single-choice print variants`,
        );
      }
    }
  };
  const referencedContent = [
    ...doc.pages,
    ...(doc.masters ?? []).flatMap((master) =>
      master.items.map((item) => ({
        ...item,
        id: `master ${master.id}/${item.id}`,
      })),
    ),
    ...doc.pages.flatMap((page) =>
      // Hidden content remains stored and can be restored. Validate its effective
      // component/variant binding as well as currently visible composition.
      resolveMasterAssignment(doc, {
        ...page,
        masterOverrides: page.masterOverrides?.map((override) => ({
          ...override,
          hidden: false,
        })),
      }).items.map((item) => ({
        ...item,
        id: `resolved ${page.id}/${item.id}`,
      })),
    ),
  ];
  for (const page of referencedContent) {
    if (page.componentId === null) continue;
    const component = site.components.find((c) => c.uuid === page.componentId);
    if (!component)
      throw new Error(`Page ${page.id} references a missing component`);
    if (page.printState.printContext !== undefined) {
      resolvePublishingPrintContext(site as Site, component as Component, page.printState.printContext);
    }
    validateChoices(
      component.variantGroups ?? [],
      page.printState.variantIds,
      page.id,
    );
    validateChoices(
      site.globalVariantGroups ?? [],
      page.printState.globalVariantIds,
      page.id,
    );
    const variants = new Set([
      ...(component.variants ?? []).map((v) => v.uuid),
      ...(component.variantGroups ?? []).flatMap((g) =>
        g.variants.map((v) => v.uuid),
      ),
    ]);
    for (const variant of page.printState.variantIds) {
      if (!variants.has(variant))
        throw new Error(`Page ${page.id} references a missing print variant`);
    }
    for (const variant of page.printState.globalVariantIds) {
      if (!globals.has(variant))
        throw new Error(
          `Page ${page.id} references a missing global print variant`,
        );
    }
  }
}

/** Run inside the original native recorder. A referenced parameter's type,
 * export or naming change must roll back with the original model transaction.
 * Legacy documents and unrelated native edits retain their original behavior. */
export function assertPublishingPrintContextReferencesAfterChange(site: PublishingSite) {
  const raw = site.flags[DOCUMENT_KEY];
  if (typeof raw !== "string") return;
  let version: unknown;
  try { version = JSON.parse(raw)?.version; } catch { return; }
  if (version !== 3) return;
  const document = readPublishingDocument(site);
  if (document) validatePublishingReferences(site, document);
}

/** Caller uses the native transaction; validate before changing any document state. */
export function writePublishingDocument(
  site: PublishingSite,
  input: PublishingDocument,
) {
  const doc = publishingDocumentSchema.parse(input);
  validatePublishingReferences(site, doc);
  site.flags[DOCUMENT_KEY] = JSON.stringify(doc);
}

/** A pure v1 conversion. Explicit document order replaces tied legacy order numbers. */
export function migrateLegacyBook(
  site: PublishingSite,
): PublishingDocument | undefined {
  const existing = readPublishingDocument(site);
  if (existing) return existing;
  const book = readBook(site);
  if (!book) return undefined;
  const zero = { top: 0, right: 0, bottom: 0, left: 0 };
  return publishingDocumentSchema.parse({
    version: 2,
    title: book.title,
    defaults: {
      trim: { width: book.widthMm, height: book.heightMm },
      bleed: zero,
      slug: zero,
      margins: {
        top: book.marginMm,
        outer: book.marginMm,
        bottom: book.marginMm,
        inner: book.marginMm,
      },
    },
    binding: {
      direction: "ltr",
      firstSide: "right",
      facing: true,
      startNumber: 1,
    },
    units: book.units,
    pages: getBookLeaves(site).map((leaf) => ({
      id: `page-${leaf.componentId}`,
      componentId: leaf.componentId,
      unitId: leaf.unitId,
      lessonId: leaf.lessonId,
      geometry: {},
      printState: { variantIds: [], globalVariantIds: [] },
    })),
  });
}

/** Called by native cloneSite with its actual identity maps, never guessed by
 * name. Guides, grids, spread guides and sections carry verbatim: occurrence
 * ids (anchors and section starts) are retained by the clone, so they stay
 * valid without remapping. */
export function remapPublishingDocumentForClone(
  from: PublishingSite,
  to: PublishingSite,
  componentIds: Map<string, string>,
  variantIds: Map<string, string>,
  frameIds: Map<string, string> = new Map(),
  paramIds: Map<string, string> = new Map(),
) {
  // Resolve legacy ties against source identities; native clones have new
  // component UUIDs. Store that explicit occurrence order only on
  // the clone; the original legacy flags and native content remain untouched.
  const doc = migrateLegacyBook(from);
  if (!doc) return;
  const required = (map: Map<string, string>, key: string, kind: string) => {
    const mapped = map.get(key);
    if (!mapped)
      throw new Error(`Cannot clone publishing ${kind} reference ${key}`);
    return mapped;
  };
  const remapPrintState = (state: PublishingPage["printState"]) => ({
    ...(state.printContext !== undefined ? { printContext: {
      ...state.printContext,
      props: state.printContext.props.map(prop => ({ ...prop, paramId: required(paramIds, prop.paramId, "parameter") })),
    }} : {}),
    variantIds: state.variantIds.map((v) => required(variantIds, v, "variant")),
    globalVariantIds: state.globalVariantIds.map((v) =>
      required(variantIds, v, "global variant"),
    ),
  });
  writePublishingDocument(to, {
    ...doc,
    ...(doc.masters
      ? {
          masters: doc.masters.map((master) => ({
            ...master,
            items: master.items.map((item) => ({
              ...item,
              componentId: required(
                componentIds,
                item.componentId,
                "master component",
              ),
              printState: remapPrintState(item.printState),
            })),
          })),
        }
      : {}),
    pages: doc.pages.map((page) => ({
      ...page,
      componentId:
        page.componentId === null
          ? null
          : required(componentIds, page.componentId, "component"),
      printState: remapPrintState(page.printState),
      ...(page.masterOverrides
        ? {
            masterOverrides: page.masterOverrides.map((override) => ({
              ...override,
              ...(override.componentId !== undefined
                ? {
                    componentId: required(
                      componentIds,
                      override.componentId,
                      "master override component",
                    ),
                  }
                : {}),
              ...(override.printState !== undefined
                ? { printState: remapPrintState(override.printState) }
                : {}),
            })),
          }
        : {}),
    })),
  });
  // Canvas frames are reconstructible editing adapters, never canonical
  // content. A persisted adapter cache may outlive a natively removed frame.
  // Retain surviving clone mappings; reconciliation recreates missing adapters.
  const remapDerivedFrames = (frames: Record<string, string>) =>
    Object.fromEntries(Object.entries(frames).flatMap(([key, frame]) => {
      const mapped = frameIds.get(frame);
      return mapped ? [[key, mapped]] : [];
    }));
  const rawView = from.flags[PUBLISHING_VIEW_KEY];
  if (typeof rawView === "string") {
    const view = publishingViewSchema.parse(JSON.parse(rawView));
    to.flags[PUBLISHING_VIEW_KEY] = JSON.stringify({
      id: view.id,
      frames: remapDerivedFrames(view.frames),
      masterFrames: Object.fromEntries(
        Object.entries(view.masterFrames).map(([page, items]) => [
          page,
          remapDerivedFrames(items),
        ]),
      ),
    });
  }
  const rawMasterView = from.flags[PUBLISHING_MASTER_VIEW_KEY];
  if (typeof rawMasterView === "string") {
    const view = publishingMasterViewSchema.parse(JSON.parse(rawMasterView));
    to.flags[PUBLISHING_MASTER_VIEW_KEY] = JSON.stringify({
      id: view.id,
      selectedMasterId: view.selectedMasterId,
      frames: Object.fromEntries(Object.entries(view.frames).map(([master, items]) => [master,
        Object.fromEntries(Object.entries(items).map(([item, sides]) => [item,
          remapDerivedFrames(sides)
        ]))
      ])),
    });
  }

}
